import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { Tabs, Input, DatePicker, Button, Space, Card, Avatar, Typography, Modal, ConfigProvider } from 'antd';
import { SearchOutlined, ReloadOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import locale from 'antd/locale/zh_CN';
import { chatManagementPageModel } from '../pageModels/ChatManagementPageModel';
import { ChatMessageDTO, CardContent } from '../../models/Chat';
import { globalStore } from '../../models/globalStore';
import { ImagePreview } from '../../components/ImagePreview';
import '../styles/ChatManagement.css';

const { RangePicker } = DatePicker;
const { Text } = Typography;

// 双方对话管理Tab组件
const RoomChatTab: React.FC<{
    onSearch: () => void;
    onReset: () => void;
    renderCardContent: (content: string) => React.ReactNode;
}> = observer(({ onSearch, onReset, renderCardContent }) => {
    return (
        <div className="room-chat-tab">
            <div className="filter-section">
                <Space wrap>
                    <Input
                        placeholder="买家手机号/ID"
                        value={chatManagementPageModel.queryParams.buyerId || ''}
                        onChange={(e) => {
                            chatManagementPageModel.queryParams = { ...chatManagementPageModel.queryParams, buyerId: e.target.value };
                        }}
                        style={{ width: 200 }}
                    />
                    <Input
                        placeholder="卖家手机号/ID"
                        value={chatManagementPageModel.queryParams.sellerId || ''}
                        onChange={(e) => {
                            chatManagementPageModel.queryParams = { ...chatManagementPageModel.queryParams, sellerId: e.target.value };
                        }}
                        style={{ width: 200 }}
                    />
                    <RangePicker
                        onChange={(dates) => {
                            if (dates && dates[0] && dates[1]) {
                                chatManagementPageModel.queryParams = {
                                    ...chatManagementPageModel.queryParams,
                                    startTime: dates[0].toDate(),
                                    endTime: dates[1].toDate()
                                };
                            } else {
                                const { startTime, endTime, ...rest } = chatManagementPageModel.queryParams;
                                chatManagementPageModel.queryParams = rest;
                            }
                        }}
                    />
                    <Button type="primary" icon={<SearchOutlined />} onClick={onSearch}>
                        搜索
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={onReset}>
                        重置
                    </Button>
                </Space>
            </div>
            <div className="chat-display-section">
                <Card title="对话记录" style={{ minHeight: 500 }}>
                    <div className="chat-messages">
                        {chatManagementPageModel.messages.map((message) => (
                            <div key={message.id} className={`message-item ${message.senderId === message.sellerId ? 'seller-message' : 'buyer-message'}`}>
                                <div className="message-header">
                                    <Avatar src={message.senderId === message.buyerId ? message.buyerAvatarUrl : message.sellerAvatarUrl} />
                                    <div className="message-info">
                                        <span className="user-info">
                                            {message.senderId === message.buyerId ? 
                                                `买家 ${message.buyerPhone}` : 
                                                `卖家 ${message.sellerPhone}`
                                            }
                                        </span>
                                        <span className="message-time">
                                            {message.time.toLocaleString()}
                                        </span>
                                    </div>
                                </div>
                                <div className="message-content">
                                    {message.isCard ? renderCardContent(message.content) : message.content}
                                </div>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>
        </div>
    );
});

// 卖家消息管理Tab组件  
const SellerMessageTab: React.FC<{
    onSearch: () => void;
    onReset: () => void;
    renderCardContent: (content: string) => React.ReactNode;
}> = observer(({ onSearch, onReset, renderCardContent }) => {
    return (
        <div className="seller-message-tab">
            <div className="filter-section">
                <Space wrap>
                    <Input
                        placeholder="卖家手机号/ID"
                        value={chatManagementPageModel.sellerQueryParams.sellerId || ''}
                        onChange={(e) => {
                            chatManagementPageModel.sellerQueryParams = { ...chatManagementPageModel.sellerQueryParams, sellerId: e.target.value };
                        }}
                        style={{ width: 200 }}
                    />
                    <RangePicker
                        onChange={(dates) => {
                            if (dates && dates[0] && dates[1]) {
                                chatManagementPageModel.sellerQueryParams = {
                                    ...chatManagementPageModel.sellerQueryParams,
                                    startTime: dates[0].toDate(),
                                    endTime: dates[1].toDate()
                                };
                            } else {
                                const { startTime, endTime, ...rest } = chatManagementPageModel.sellerQueryParams;
                                chatManagementPageModel.sellerQueryParams = rest;
                            }
                        }}
                    />
                    <Button type="primary" icon={<SearchOutlined />} onClick={onSearch}>
                        搜索
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={onReset}>
                        重置
                    </Button>
                </Space>
            </div>
            <div className="message-display-section">
                <Card title="卖家消息列表" style={{ minHeight: 500 }}>
                    <div className="message-list">
                        {chatManagementPageModel.sellerMessages.map((message) => (
                            <div key={message.id} className="message-list-item">
                                <Text>
                                    {message.time.toLocaleString()} 卖家{message.sellerPhone} 发给 买家{message.buyerPhone} {message.isCard ? renderCardContent(message.content) : message.content}
                                </Text>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>
        </div>
    );
});

// 买家消息管理Tab组件
const BuyerMessageTab: React.FC<{
    onSearch: () => void;
    onReset: () => void;
    renderCardContent: (content: string) => React.ReactNode;
}> = observer(({ onSearch, onReset, renderCardContent }) => {
    return (
        <div className="buyer-message-tab">
            <div className="filter-section">
                <Space wrap>
                    <Input
                        placeholder="买家手机号/ID"
                        value={chatManagementPageModel.buyerQueryParams.buyerId || ''}
                        onChange={(e) => {
                            chatManagementPageModel.buyerQueryParams = { ...chatManagementPageModel.buyerQueryParams, buyerId: e.target.value };
                        }}
                        style={{ width: 200 }}
                    />
                    <RangePicker
                        onChange={(dates) => {
                            if (dates && dates[0] && dates[1]) {
                                chatManagementPageModel.buyerQueryParams = {
                                    ...chatManagementPageModel.buyerQueryParams,
                                    startTime: dates[0].toDate(),
                                    endTime: dates[1].toDate()
                                };
                            } else {
                                const { startTime, endTime, ...rest } = chatManagementPageModel.buyerQueryParams;
                                chatManagementPageModel.buyerQueryParams = rest;
                            }
                        }}
                    />
                    <Button type="primary" icon={<SearchOutlined />} onClick={onSearch}>
                        搜索
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={onReset}>
                        重置
                    </Button>
                </Space>
            </div>
            <div className="message-display-section">
                <Card title="买家消息列表" style={{ minHeight: 500 }}>
                    <div className="message-list">
                        {chatManagementPageModel.buyerMessages.map((message) => (
                            <div key={message.id} className="message-list-item">
                                <Text>
                                    {message.time.toLocaleString()} 买家{message.buyerPhone} 发给 卖家{message.sellerPhone} {message.isCard ? renderCardContent(message.content) : message.content}
                                </Text>
                            </div>
                        ))}
                    </div>
                </Card>
            </div>
        </div>
    );
});

const ChatManagement: React.FC = () => {
    const [loading, setLoading] = useState(false);
    const [imagePreviewVisible, setImagePreviewVisible] = useState(false);
    const [previewImageUrl, setPreviewImageUrl] = useState<string>('');

    useEffect(() => {
        globalStore.selectedMenuKey = 'chat-management';
        handleLoadData();
    }, []);

    const handleTabChange = (key: string) => {
        chatManagementPageModel.currentTab = parseInt(key);
        handleLoadData();
    };

    const handleSearch = () => {
        handleLoadData();
    };

    const handleReset = () => {
        chatManagementPageModel.resetQueryParams();
        handleLoadData();
    };

    const handleLoadData = async () => {
        setLoading(true);
        try {
            await chatManagementPageModel.loadDataByCurrentTab();
        } finally {
            setLoading(false);
        }
    };

    const renderCardContent = (content: string) => {
        try {
            const cardData: CardContent = JSON.parse(content);
            switch (cardData.type) {
                case 'product':
                    return `[商品卡片]商品ID：${cardData.productId}`;
                case 'order':
                    return `[订单卡片]订单ID：${cardData.orderId}`;
                case 'image':
                    return (
                        <span 
                            style={{ color: '#1890ff', cursor: 'pointer' }}
                            onClick={() => {
                                setPreviewImageUrl(cardData.url || '');
                                setImagePreviewVisible(true);
                            }}
                        >
                            [图片] 查看图片
                        </span>
                    );
                case 'quickContract':
                    return `[闪电合约卡片] 合约ID：${cardData.contractId}`;
                default:
                    return content;
            }
        } catch {
            return content;
        }
    };

    return (
        <ConfigProvider locale={locale}>
            <div className="chat-management">
                <Tabs
                    activeKey={chatManagementPageModel.currentTab.toString()}
                    onChange={handleTabChange}
                    items={[
                        {
                            key: '0',
                            label: '双方对话管理',
                            children: <RoomChatTab onSearch={handleSearch} onReset={handleReset} renderCardContent={renderCardContent} />
                        },
                        {
                            key: '1',
                            label: '卖家消息管理',
                            children: <SellerMessageTab onSearch={handleSearch} onReset={handleReset} renderCardContent={renderCardContent} />
                        },
                        {
                            key: '2',
                            label: '买家消息管理',
                            children: <BuyerMessageTab onSearch={handleSearch} onReset={handleReset} renderCardContent={renderCardContent} />
                        }
                    ]}
                />
                
                <ImagePreview 
                    visible={imagePreviewVisible}
                    src={previewImageUrl}
                    onClose={() => setImagePreviewVisible(false)}
                />
            </div>
        </ConfigProvider>
    );
};

export default observer(ChatManagement); 